import { useState, useEffect, useCallback, useRef } from 'react';
import SaleApiClient from '../services/SaleApiClient';

/**
 * Sale Order Workflow hook - handles all Sale Order workflow execution logic
 * Completely separate from useV2Workflow - uses /api/sale/workflows endpoints
 */
export const useSaleWorkflow = (workflowId, token, initialRunId = null) => {
  // Core state
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [runId, setRunId] = useState(initialRunId);
  const [executionStatus, setExecutionStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [lastEventTimestamp, setLastEventTimestamp] = useState(null);

  // Services
  const apiClientRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastEventTimestampRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const pollingPausedRef = useRef(false);  // Pause flag to prevent polling when modal is shown

  // Helper function to process messages
  // includeUserMessages: true when loading historical chat, false during active polling (user messages added locally)
  const processMessagesWithTiming = useCallback((newEvents, skipDelay = false, includeUserMessages = false) => {
    console.log('📦 SaleWorkflow: Processing events', newEvents.length, 'includeUser:', includeUserMessages);

    // Only include 'user' messages when loading historical chat (not during active polling)
    const allowedTypes = includeUserMessages
      ? ['user', 'assistant', 'tool_call', 'tool_result', 'workflow_step']
      : ['assistant', 'tool_call', 'tool_result', 'workflow_step'];

    const chatMessages = newEvents
      .filter(update => update.type === 'chat_message' &&
             allowedTypes.includes(update.data.message_type))
      .map(update => ({
        id: update.data.message_id,
        type: update.data.message_type,
        content: update.data.content,
        timestamp: update.data.timestamp,
        step_name: update.data.step_name,
        tool_name: update.data.tool_name
      }));

    // Also process workflow events that contain UI component definitions
    const uiComponentEvents = newEvents
      .filter(update => update.type === 'workflow_event' &&
             update.data.event_type === 'step.completed' &&
             update.data.data?.structured_output &&
             update.data.data.structured_output.component_definition)
      .map(update => {
        const stableId = `ui_${update.data.data.step_name}_${update.data.timestamp}`;
        return {
          id: stableId,
          type: 'assistant',
          content: update.data.data.structured_output,
          timestamp: update.data.timestamp,
          step_name: update.data.data.step_name,
          isUIComponent: true
        };
      });

    // Combine and sort all messages
    const allMessages = [...chatMessages, ...uiComponentEvents]
      .sort((a, b) => {
        const timeA = new Date(a.timestamp);
        const timeB = new Date(b.timestamp);

        if (Math.abs(timeA - timeB) < 2000) {
          if (a.type === 'tool_call' && b.type === 'tool_result') return -1;
          if (a.type === 'tool_result' && b.type === 'tool_call') return 1;
        }

        return timeA - timeB;
      });

    if (allMessages.length === 0) return;

    // Add messages immediately
    setMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const newMessages = allMessages.filter(msg => !existingIds.has(msg.id));
      return [...prev, ...newMessages];
    });
  }, []);

  // Polling mechanism
  const startPollingForResults = useCallback((currentRunId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log('🔄 SaleWorkflow: Starting polling for run', currentRunId);
    pollingStartTimeRef.current = Date.now();

    pollingIntervalRef.current = setInterval(async () => {
      // Check if polling is paused (modal is shown)
      if (pollingPausedRef.current) {
        console.log('⏸️ SaleWorkflow: Polling paused, skipping tick');
        return;
      }

      try {
        const status = await apiClientRef.current.getWorkflowStatus(currentRunId);

        if (status && status.success && status.data) {
          const workflowRun = status.data.workflow_run;
          const currentStatus = workflowRun ? workflowRun.status : null;
          const stepName = workflowRun ? workflowRun.current_step : null;

          if (currentStatus) setExecutionStatus(currentStatus);
          if (stepName) setCurrentStep(stepName);

          // Fetch workflow events
          try {
            const events = await apiClientRef.current.getWorkflowEvents(currentRunId, lastEventTimestampRef.current);
            if (events && events.success && events.updates) {
              processMessagesWithTiming(events.updates, true);

              if (events.updates.length > 0) {
                const latestTimestamp = events.updates[events.updates.length - 1].timestamp;
                lastEventTimestampRef.current = latestTimestamp;
                setLastEventTimestamp(latestTimestamp);
              }
            }
          } catch (eventsError) {
            console.error('❌ SaleWorkflow: Error fetching events:', eventsError);
          }

          // Stop polling if workflow is in a terminal state
          if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'cancelled' || currentStatus === 'paused') {
            console.log('🏁 SaleWorkflow: Workflow completed with status:', currentStatus);
            setIsLoading(false);

            // Final fetches
            const completedRunId = currentRunId;
            let fetchCount = 0;
            const maxFetches = 6;
            const fetchInterval = 500;

            const doFinalFetch = async () => {
              if (completedRunId !== runId) return;

              fetchCount++;
              try {
                const finalEvents = await apiClientRef.current.getWorkflowEvents(currentRunId, null);
                if (finalEvents && finalEvents.success && finalEvents.updates && finalEvents.updates.length > 0) {
                  processMessagesWithTiming(finalEvents.updates, true);

                  const latestTimestamp = finalEvents.updates[finalEvents.updates.length - 1].timestamp;
                  lastEventTimestampRef.current = latestTimestamp;
                  setLastEventTimestamp(latestTimestamp);
                }

                if (fetchCount < maxFetches) {
                  setTimeout(doFinalFetch, fetchInterval);
                } else {
                  if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                  }
                }
              } catch (finalError) {
                console.error('❌ SaleWorkflow: Error in final fetch:', finalError);
                if (fetchCount >= maxFetches && pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              }
            };

            doFinalFetch();
          }
        }
      } catch (error) {
        console.error('❌ SaleWorkflow: Polling error:', error);
      }
    }, 250);
  }, [runId, processMessagesWithTiming]);

  // Initialize services
  useEffect(() => {
    if (!token) return;

    apiClientRef.current = new SaleApiClient(token);

    if (initialRunId) {
      console.log('🔄 SaleWorkflow: Continuing existing workflow with run_id:', initialRunId);
      setExecutionStatus('running');
      setIsLoading(true);

      // Don't show any welcome message for resumed workflows - just load the messages
      (async () => {
        try {
          const allEvents = await apiClientRef.current.getWorkflowEvents(initialRunId, null);

          if (allEvents && allEvents.success && allEvents.updates) {
            // Load historical - include user messages from DB
            processMessagesWithTiming(allEvents.updates, true, true);

            if (allEvents.updates.length > 0) {
              const latestTimestamp = allEvents.updates[allEvents.updates.length - 1].timestamp;
              lastEventTimestampRef.current = latestTimestamp;
              setLastEventTimestamp(latestTimestamp);
            }
          }

          startPollingForResults(initialRunId);
        } catch (error) {
          console.error('❌ SaleWorkflow: Error loading existing messages:', error);
          startPollingForResults(initialRunId);
        }
      })();
    } else {
      setMessages([{
        id: 'welcome',
        type: 'system',
        content: `🚀 Ready to start Sale Order workflow. Send a message to begin!`,
        timestamp: new Date().toISOString()
      }]);
      setExecutionStatus('ready');
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [token, initialRunId, startPollingForResults, processMessagesWithTiming]);

  // Send message function
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || !apiClientRef.current) return;

    if (!runId && isLoading) {
      console.log('⚠️ SaleWorkflow: Blocked duplicate workflow start');
      return;
    }

    setIsLoading(true);
    setError(null);

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      if (!runId) {
        console.log('🚀 SaleWorkflow: Starting new workflow', { workflowId, messageText });

        const result = await apiClientRef.current.startWorkflow(workflowId, messageText);

        if (result.success && result.run_id) {
          lastEventTimestampRef.current = null;
          setLastEventTimestamp(null);
          setMessages([userMessage]);
          setEvents([]);
          setError(null);

          setRunId(result.run_id);
          setExecutionStatus('running');

          startPollingForResults(result.run_id);

          setTimeout(async () => {
            try {
              const quickCheck = await apiClientRef.current.getWorkflowEvents(result.run_id, lastEventTimestampRef.current);
              if (quickCheck && quickCheck.success && quickCheck.updates) {
                processMessagesWithTiming(quickCheck.updates, true);

                if (quickCheck.updates.length > 0) {
                  const latestTimestamp = quickCheck.updates[quickCheck.updates.length - 1].timestamp;
                  lastEventTimestampRef.current = latestTimestamp;
                  setLastEventTimestamp(latestTimestamp);
                }
              }
            } catch (quickError) {
              console.log('⚡ SaleWorkflow: Quick response check failed');
            }
          }, 100);
        } else {
          throw new Error('Failed to start workflow - no run_id received');
        }
      } else {
        console.log('🔄 SaleWorkflow: Continuing workflow', { runId, messageText });

        const result = await apiClientRef.current.continueWorkflow(runId, messageText);

        if (result.success) {
          setExecutionStatus('running');

          // Reset timestamp to fetch all new events from this continuation
          // This ensures messages created during continue are not filtered out
          lastEventTimestampRef.current = null;
          setLastEventTimestamp(null);

          startPollingForResults(runId);

          setTimeout(async () => {
            try {
              // Fetch ALL events (no timestamp filter) to get new messages
              const quickCheck = await apiClientRef.current.getWorkflowEvents(runId, null);
              if (quickCheck && quickCheck.success && quickCheck.updates) {
                processMessagesWithTiming(quickCheck.updates, true);

                if (quickCheck.updates.length > 0) {
                  const latestTimestamp = quickCheck.updates[quickCheck.updates.length - 1].timestamp;
                  lastEventTimestampRef.current = latestTimestamp;
                  setLastEventTimestamp(latestTimestamp);
                }
              }
            } catch (quickError) {
              console.log('⚡ SaleWorkflow: Quick continue check failed');
            }
          }, 100);
        } else {
          throw new Error('Failed to continue workflow');
        }
      }
    } catch (err) {
      console.error('❌ SaleWorkflow: Failed to send message', err);

      const errorMessage = {
        id: `error_${Date.now()}`,
        type: 'error',
        content: `❌ Error: ${err.message}`,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(err.message);
      setExecutionStatus('failed');
      setIsLoading(false);
    }
  }, [workflowId, runId, isLoading, startPollingForResults, processMessagesWithTiming]);

  // Reset workflow
  const resetWorkflow = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setRunId(null);
    setMessages([{
      id: 'welcome',
      type: 'system',
      content: `🚀 Ready to start Sale Order workflow. Send a message to begin!`,
      timestamp: new Date().toISOString()
    }]);
    setExecutionStatus('ready');
    setEvents([]);
    setError(null);
    setIsLoading(false);
    setLastEventTimestamp(null);
    lastEventTimestampRef.current = null;
  }, []);

  // Stop polling (when Review modal is shown)
  const stopPolling = useCallback(() => {
    console.log('⏸️ SaleWorkflow: Pausing polling');
    pollingPausedRef.current = true;  // Set pause flag
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Resume polling (when Create Sales Order is clicked)
  const resumePolling = useCallback(() => {
    console.log('▶️ SaleWorkflow: Resuming polling');
    pollingPausedRef.current = false;  // Clear pause flag
    if (runId && !pollingIntervalRef.current) {
      startPollingForResults(runId);
    }
  }, [runId, startPollingForResults]);

  return {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    events,
    error,
    sendMessage,
    resetWorkflow,
    hasActiveWorkflow: !!runId,
    canSendMessage: !isLoading && !!token,
    stopPolling,
    resumePolling
  };
};
